import {
  CreditHistory,
  CreditOperationRequest,
  CreditOperationResponse
} from '@/types/model';
import { PaginatedResponse, apiClient, QueryParams } from '@/services/apiClient';

export interface CreditHistoryQueryParams extends QueryParams {
  username?: string;
  'targetUser.id'?: string;
  operationType?: string;
  creditType?: string;
  dateFrom?: string;
  dateTo?: string;
  'performedByUser.id'?: string;
}

export interface CreditSalesReportResponse {
  performedByUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  generatedAt: string;
  operationCount: number;
  netCreditsSold: number;
  pricePer12Credits: number;
  pricePer12CreditsCurrency: 'USD' | 'DOP';
  totalAmountDue: number;
  operations: CreditHistory[];
}

export interface CreateDemoRequest {
  userId: number;
  targetUsername?: string;
  reason?: string;
}

export interface PurchaseDemoCreditsResponse {
  success: boolean;
  message: string;
}

export const creditService = {
  /**
   * Add normal credits to a user or agent
   */
  async addCredits(data: CreditOperationRequest): Promise<CreditOperationResponse> {
    return apiClient.post<CreditOperationResponse>('/credits/add', data);
  },

  /**
   * Add demo credits to a user
   */
  async addDemoCredits(data: CreateDemoRequest): Promise<CreditOperationResponse> {
    return apiClient.post<CreditOperationResponse>(
      `/credits/demo/${data.userId}`,
      undefined,
      { params: { reason: data.reason } }
    );
  },

  /**
   * Purchase demo credits (30 demo credits for 1 sale credit)
   */
  async purchaseDemoCredits(): Promise<PurchaseDemoCreditsResponse> {
    return apiClient.post<PurchaseDemoCreditsResponse>('/credits/demo/purchase');
  },

  /**
   * Deduct normal credits from a user or agent
   */
  async deductCredits(data: CreditOperationRequest): Promise<CreditOperationResponse> {
    return apiClient.post<CreditOperationResponse>('/credits/deduct', data);
  },

  /**
   * Add sale credits to an agent
   */
  async addCreditsForSale(data: CreditOperationRequest): Promise<CreditOperationResponse> {
    return apiClient.post<CreditOperationResponse>('/credits/add-for-sale', data);
  },

  /**
   * Deduct sale credits from an agent
   */
  async deductCreditsForSale(data: CreditOperationRequest): Promise<CreditOperationResponse> {
    return apiClient.post<CreditOperationResponse>('/credits/deduct-for-sale', data);
  },

  /**
   * Get credit history for a specific user
   */
  async getCreditHistory(username: string, params: QueryParams = {}): Promise<PaginatedResponse<CreditHistory>> {
    return apiClient.getPaginated<CreditHistory>(`/credits/history/${username}`, params);
  },

  /**
   * Get recent credit history for a user (last 50 records)
   */
  async getRecentCreditHistory(username: string): Promise<CreditHistory[]> {
    return apiClient.get<CreditHistory[]>(`/credits/history/${username}/recent`);
  },

  /**
   * Get all credit history with pagination (admin only)
   */
  async getAllCreditHistory(params: QueryParams = {}): Promise<PaginatedResponse<CreditHistory>> {
    return apiClient.getPaginated<CreditHistory>('/credits', params);
  },

  async getCreditSalesReport(params: CreditHistoryQueryParams = {}): Promise<CreditSalesReportResponse> {
    return apiClient.get<CreditSalesReportResponse>('/credits/reports/sales', params);
  },

  /**
   * Get credit operations count for a user
   */
  async getCreditOperationsCount(username: string): Promise<{ username: string; operationsCount: number }> {
    return apiClient.get<{ username: string; operationsCount: number }>(`/credits/history/${username}/count`);
  }
};
